import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Activity, ChevronLeft, ChevronRight, Search, User, Clock,
  Plus, Pencil, Trash2, Check, X, KeyRound,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

const ACTION_STYLES = {
  create: { icon: Plus, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  update: { icon: Pencil, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  delete: { icon: Trash2, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  approve: { icon: Check, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  reject: { icon: X, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  change_password: { icon: KeyRound, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
};

const TARGET_LABELS = {
  product: 'Product',
  review: 'Review',
  order: 'Order',
  inventory: 'Inventory',
  custom_order: 'Custom Order',
  custom_design: 'Custom Design',
  bulk_order: 'Bulk Order',
  admin_user: 'Admin User',
};

const AdminAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_action: {}, by_target_type: {} });
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/audit-log/?limit=1000`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/admin/audit-log/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load audit log');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [actionFilter, targetFilter, search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((log) => {
      if (actionFilter !== 'all' && log.action !== actionFilter) return false;
      if (targetFilter !== 'all' && log.target_type !== targetFilter) return false;
      if (q) {
        const haystack = `${log.admin_email || ''} ${log.target_name || ''} ${log.details || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [logs, actionFilter, targetFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  const renderActionPill = (action) => {
    const style = ACTION_STYLES[action] || { icon: Activity, color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30' };
    const Icon = style.icon;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.color} ${style.border}`}
      >
        <Icon className="w-3 h-3" />
        {action}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading audit log...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Activity className="w-8 h-8" />
          Audit Log
        </h1>
        <p className="text-gray-400">
          Every admin action — who, what, and when. Useful for accountability across multiple admins.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Total Actions</p>
            <p className="text-3xl font-bold text-white mt-1" data-testid="audit-total">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-green-500/30">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Creates / Approves</p>
            <p className="text-3xl font-bold text-green-400 mt-1">
              {(stats.by_action.create || 0) + (stats.by_action.approve || 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-blue-500/30">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Updates</p>
            <p className="text-3xl font-bold text-blue-400 mt-1">
              {stats.by_action.update || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-red-500/30">
          <CardContent className="p-5">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Deletes / Rejects</p>
            <p className="text-3xl font-bold text-red-400 mt-1">
              {(stats.by_action.delete || 0) + (stats.by_action.reject || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-gray-900 border border-gray-800 rounded-lg p-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search admin email, target, or details..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none"
            data-testid="audit-search"
          />
        </div>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700"
          data-testid="audit-action-filter"
        >
          <option value="all">All actions</option>
          {Object.keys(ACTION_STYLES).map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={targetFilter}
          onChange={(e) => setTargetFilter(e.target.value)}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700"
          data-testid="audit-target-filter"
        >
          <option value="all">All targets</option>
          {Object.entries(TARGET_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Logs table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          {paginated.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">
                {logs.length === 0
                  ? 'No actions logged yet. Take an admin action — it will appear here.'
                  : 'No entries match your filters.'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wide">
                  <th className="px-6 py-3">When</th>
                  <th className="px-6 py-3">Admin</th>
                  <th className="px-6 py-3">Action</th>
                  <th className="px-6 py-3">Target</th>
                  <th className="px-6 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {paginated.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-800/30 transition-colors" data-testid="audit-row">
                    <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-gray-500" />
                        {formatTime(log.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-gray-500" />
                        <span className="font-medium">{log.admin_email || '(unknown)'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">{renderActionPill(log.action)}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">
                          {TARGET_LABELS[log.target_type] || log.target_type}
                        </span>
                        <span className="font-medium text-white">
                          {log.target_name || log.target_id || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {log.details || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between bg-gray-900 border border-gray-800 rounded-lg px-6 py-4">
          <p className="text-sm text-gray-400">
            Showing{' '}
            <span className="font-medium text-white">{(currentPage - 1) * PAGE_SIZE + 1}</span>
            {' '}to{' '}
            <span className="font-medium text-white">
              {Math.min(currentPage * PAGE_SIZE, filtered.length)}
            </span>
            {' '}of <span className="font-medium text-white">{filtered.length}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="audit-prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-gray-400 px-2">
              Page <span className="font-medium text-white">{currentPage}</span> of{' '}
              <span className="font-medium text-white">{totalPages}</span>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              data-testid="audit-next-page"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLog;
